import React, {
  Fragment,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HospitalBedIcon } from "assets/images";
import useServiceStore from "store/serviceStore";
import { GRAINS, pixelPerInch } from "constants/appConstant";
import Wrapper from "./headwall-components/wrapper";
import { HorizontalRuler, HospitalBed } from "components";
import {
  alterImageUrl,
  calculateHeight,
  calculateWidth,
} from "config/calculations/calculation";

const Axiom = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    setDragEntered,
    clickArrowLeft,
    clickArrowRight,
    clickArrowUp,
    clickArrowDown,
    service,
    showBedGraphic,
  } = props;
  const wrapperRef = useRef(null);

  const { serv, view_only, setService } = useServiceStore();
  const [railInd, setRailInd] = useState(0);
  const [rowInd, setRowInd] = useState(0);

  const [customEvent, setCustomEvent] = useState(null);
  const {
    headwalls,
    width,
    ceiling_height,
    sub_model,
    model,
    accessory_rails,
  } = values;
  const axiomWidth = useMemo(() => {
    return (width.ft * 12 + width.in - 3.25) / pixelPerInch; //3.25in for borders
  }, [width]);

  const axiomHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in - 12.63) / pixelPerInch //12.63 from bottom
    );
  }, [ceiling_height]);

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  const wallWidth = useMemo(() => {
    //added 10 inches extra to manage wall width
    if (headwalls.length >= 2)
      return (
        Math.round((width.ft * 12 + width.in + 10) / pixelPerInch) * 2 + 1450
      );
    //700 px for image
    else return Math.round((width.ft * 12 + width.in + 10) / pixelPerInch);
  }, [width, headwalls]);

  const rulerWidth = useMemo(() => {
    //added 5 ft for bed
    let totalFeet = 0;
    let totalInches = 0;
    if (headwalls.length >= 2) {
      totalFeet = width.ft * 2 + 5;
      totalInches = width.in * 2;
      if (totalInches >= 12) {
        totalFeet += Math.floor(totalInches / 12);
        totalInches = totalInches % 12;
      }
    } else {
      totalFeet = width.ft;
      totalInches = width.in;
    }

    return { ft: totalFeet, in: totalInches };
  }, [width, headwalls]);

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "214.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, rulerWidth]);

  //to adjust infusion pumps and ambu bag location to fix UI
  const accessoriePosition = (acc) => {
    const xPosition = acc.positions?.x;

    if (acc.mount_type !== "track") return xPosition;

    // Handle "Single (3 hook)"
    if (acc.name === "Single (3 hook)") {
      return xPosition < 50 ? xPosition - 20 : xPosition - 20;
    }
    // Handle "Dual (6 hook)"
    if (acc.name === "Dual (6 hook)") {
      return xPosition - 22;
    }

    const extraBedSpace = 1071.42857143;
    const sectionWidth = (width.ft * 12 + width.in) / pixelPerInch;
    const panelMidPoint = sectionWidth / 2;
    const isSingleHeadwall = headwalls.length === 1;
    const isMultiHeadwall = headwalls.length > 1;

    // Handle "Ambu Bag Holder"
    if (acc.name === "Ambu Bag Holder") {
      if (isSingleHeadwall && xPosition < panelMidPoint) {
        return xPosition + 9;
      }

      if (
        isMultiHeadwall &&
        xPosition > panelMidPoint &&
        xPosition < sectionWidth + extraBedSpace / 2
      ) {
        return xPosition + 3;
      }

      if (
        isMultiHeadwall &&
        xPosition < sectionWidth + extraBedSpace + panelMidPoint
      ) {
        return xPosition + 9;
      }

      return xPosition;
    }

    if (acc.name === "J Hook") {
      if (isSingleHeadwall && xPosition < panelMidPoint) {
        return xPosition + 4;
      }

      if (
        isSingleHeadwall &&
        xPosition > panelMidPoint &&
        xPosition < sectionWidth + extraBedSpace / 2
      ) {
        return xPosition - 5;
      }
      if (
        isMultiHeadwall &&
        xPosition > panelMidPoint &&
        xPosition < sectionWidth + extraBedSpace / 2
      ) {
        return xPosition - 5;
      }

      if (
        isMultiHeadwall &&
        xPosition < sectionWidth + extraBedSpace + panelMidPoint
      ) {
        return xPosition + 4;
      }
      if (
        isMultiHeadwall &&
        xPosition > sectionWidth + extraBedSpace + panelMidPoint
      ) {
        return xPosition - 5;
      }

      return xPosition;
    }

    if (acc.name === "Single") {
      const isSingleHeadwall = headwalls.length === 1;
      const isMultiHeadwall = headwalls.length > 1;

      if (isSingleHeadwall && xPosition < panelMidPoint) {
        return xPosition - 2;
      }

      if (
        isSingleHeadwall &&
        xPosition > panelMidPoint &&
        xPosition < sectionWidth + extraBedSpace / 2
      ) {
        return xPosition - 11;
      }
      if (
        isMultiHeadwall &&
        xPosition > panelMidPoint &&
        xPosition < sectionWidth + extraBedSpace / 2
      ) {
        return xPosition - 11;
      }

      if (
        isMultiHeadwall &&
        xPosition < sectionWidth + extraBedSpace + panelMidPoint
      ) {
        return xPosition - 2;
      }
      if (
        isMultiHeadwall &&
        xPosition > sectionWidth + extraBedSpace + panelMidPoint
      ) {
        return xPosition - 11;
      }

      return xPosition;
    }

    // Default case for track-mounted accessories
    return xPosition;
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        view_only && clickArrowUp(customEvent, serv, railInd, rowInd, "axiom");
        break;
      case "ArrowDown":
        view_only &&
          clickArrowDown(customEvent, serv, railInd, rowInd, "axiom");
        break;
      case "ArrowLeft":
        view_only &&
          clickArrowLeft(customEvent, serv, railInd, rowInd, "axiom");
        break;
      case "ArrowRight":
        view_only &&
          clickArrowRight(customEvent, serv, railInd, rowInd, "axiom");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Wrapper
        ruler={ruler}
        width={wallWidth}
        wallHeight={wallHeight}
        ceiling_height={ceiling_height}
        model={model}
        ref={ref}
      >
        <div className=" flex items-end pb-[89px] relative compass-board">
          {Object.keys(accessory_rails)?.map((railKey) => {
            const railData = accessory_rails[railKey] || [];
            return (
              <>
                {railData?.map((ser, i) => (
                  <div
                    draggable
                    key={i}
                    className={`absolute flex justify-center z-10`}
                    style={{
                      left: ser.positions?.x,
                      top: ser.positions?.y,
                      cursor: "grab",
                    }}
                    onDragStart={(e) => handleDragStart(ser, e)}
                    onDragEnter={(e) => handleDragEnter(e, "rail")}
                    onDrop={(e) => handleDrop(e, "rail", 0)}
                    onDragOver={(e) => handleonDragOver(e)}
                    // onDragEnter={(e) => e.stopPropagation()}
                  >
                    {dragEntered &&
                      service?.service?.u_id !== ser?.u_id &&
                      service?.service?.is_accessory_rail &&
                      service?.service?.type !== "unimount" &&
                      ser.type !== "unimount" &&
                      ser.mount_type !== "track" &&
                      ser.mount_type !== "direct" && (
                        <div
                          className="h-[234.815714286px] top-[-107.142857143px] non_dropable w-full absolute"
                          style={{ background: "rgb(215 176 162)", zIndex: 0 }}
                        />
                      )}
                    <div
                      draggable={"false"}
                      style={{
                        width: ser.width / pixelPerInch,
                        backgroundImage: `url(${ser.image_url})`,
                        backgroundSize: ser.width < 15 ? "cover" : "contain",
                        // backgroundSize: "cover",
                        backgroundPosition: "center",
                        pointerEvents: "none",
                        height: ser.height / pixelPerInch,
                        zIndex: 1,
                      }}
                      onDragEnter={(e) => e.stopPropagation()}
                    />
                  </div>
                ))}
              </>
            );
          })}
          {/* maping accessories */}
          {Object.keys(accessory_rails)?.map((railKey) => {
            const railData = accessory_rails[railKey] || [];
            return (
              <>
                {railData?.map((ser, i) => (
                  <>
                    {ser.accessories?.map((acc, i) => (
                      <div
                        draggable
                        key={i}
                        className="absolute flex justify-center z-10"
                        style={{
                          left: acc.positions?.x,
                          top: acc.positions?.y,
                          cursor: "grab",
                        }}
                        onDragStart={(e) => handleDragStart(acc, e)}
                        onDragEnter={(e) => handleDragEnter(e)}
                        onDrop={(e) => handleDrop(e, "rail")}
                        onDragOver={(e) => handleonDragOver(e, "rail")}
                      >
                        <div
                          draggable={"false"}
                          style={{
                            width: acc.width / pixelPerInch,
                            backgroundImage: `url(${acc.image})`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            pointerEvents: "none",
                            height: acc.height / pixelPerInch,
                          }}
                          onDragEnter={(e) => e.stopPropagation()}
                        />
                      </div>
                    ))}
                  </>
                ))}
              </>
            );
          })}
          {/* maping direct*/}
          {accessory_rails["direct"]?.map((acc, i) => (
            <div
              draggable
              key={i}
              className="absolute flex justify-center z-10"
              style={{
                left: accessoriePosition(acc),
                top: acc.positions?.y,
                cursor: "grab",
              }}
              onDragStart={(e) => handleDragStart(acc, e)}
              onDragEnter={(e) => handleDragEnter(e)}
              onDrop={(e) => handleDrop(e, "rail")}
              onDragOver={(e) => handleonDragOver(e, "rail")}
            >
              <div
                draggable={"false"}
                style={{
                  width: calculateWidth(acc),
                  backgroundImage: `url(${acc.image})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  pointerEvents: "none",
                  height: calculateHeight(acc),
                }}
                onDragEnter={(e) => e.stopPropagation()}
              />
            </div>
          ))}
          {headwalls?.map((rail, railIndex) => (
            <Fragment key={railIndex}>
              {railIndex > 0 && (
                <div className="flex items-center relative px-[107.142857143px]">
                  <img
                    src={HospitalBedIcon}
                    alt="...img"
                    width="100%"
                    height="auto"
                    className=" min-w-[857.142857143px]"
                    style={{ bottom: "500px" }}
                  />
                </div>
              )}
              <div
                className={`w-max axiom_outer relative ${
                  sub_model === "flush" && "px-[7.50px]"
                }`}
                style={{ height: axiomHeight }}
                onDrop={(e) => handleDrop(e, "rail", railIndex)}
                onDragOver={(e) => handleonDragOver(e, "rail")}
                // onDragEnter={(e) => handleDragEnter(e, railIndex)}
              >
                <div
                  className={`axiom relative h-full ${
                    dragEntered ? "axiom_compartment" : ""
                  }`}
                >
                  <div
                    id="axiom_outer"
                    className={`h-full z-10 ${
                      dragEntered &&
                      !service?.service?.is_accessory &&
                      !service?.service?.is_accessory_rail
                        ? "non_dropable_axiom"
                        : ""
                    }`}
                    style={{
                      backgroundColor: rail?.color,
                      width: axiomWidth,
                      backgroundImage: `url(${GRAINS[rail?.background]})`,
                    }}
                    onDrop={(e) => handleDrop(e, "rail", railIndex)}
                    onDragOver={(e) => handleonDragOver(e, "rail")}
                    onDragEnter={(e) => handleDragEnter(e, railIndex)}
                  >
                    <Rows
                      dragEntered={dragEntered}
                      rail={rail}
                      railIndex={railIndex}
                      handleDragStart={handleDragStart}
                      setService={setService}
                      setDragEntered={setDragEntered}
                      service={service}
                      rowInd={setRowInd}
                      railInd={setRailInd}
                      customEvent={setCustomEvent}
                      wrapperRef={wrapperRef}
                    />
                  </div>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
        {ruler && (
          <HorizontalRuler
            class_name={"bottom-[14px] z-50"}
            width={rulerWidth}
            model={model}
          />
        )}
        {showBedGraphic && headwalls.length < 2 && <HospitalBed />}
      </Wrapper>
    </>
  );
});

export default Axiom;

const Rows = ({
  dragEntered,
  rail,
  railIndex,
  handleDragStart,
  setService,
  setDragEntered,
  service,
  railInd,
  rowInd,
  customEvent,
  wrapperRef,
}) => {
  const { serv, view_only } = useServiceStore();
  const rowArray = [
    ...new Set(
      rail?.services
        .filter((serv) => !serv.is_slide)
        .map((item) => item.positions.y)
    ),
  ];
  const generateStyle = (ser) => {
    const styleObj = {
      padding: servicePadding(ser),
      cursor: "grab",
      background:
        ser == serv ? (view_only ? "#75060f" : "transparent") : "transparent",
    };
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      styleObj.left = servLeft(ser);
      if (ser.is_slide) styleObj.top = ser.positions.y;
    } else styleObj.transform = servicePosition(ser);

    return styleObj;
  };

  const serviceWidth = (ser) => {
    if (ser.category === "Medical Gas Outlet" && !ser?.is_slide)
      return (ser.width + 2.375) / pixelPerInch; //2.375 for side margins
    else return (ser.width + 0.125) / pixelPerInch;
  };

  const servicePosition = (ser) => {
    if (ser?.is_slide) {
      if (!ser.positions.x) return `translate(${-32}px, ${ser.positions?.y}px)`;
      else return `translate(${ser.positions?.x - 1}px, ${ser.positions?.y}px)`;
    } else return `translate(${ser.positions?.x}px)`;
  };

  const servLeft = (ser) => {
    if (ser?.is_slide) {
      if (!ser.positions.x) return "-27px";
      else return `${ser.positions?.x - 6}px`;
    } else return `${ser.positions?.x}px`;
  };

  const servicePadding = (ser) => {
    if (!ser?.is_slide) return `13.39px 0 13.39px 0`;
    // else return `5px 0 5px 0`;
  };

  return (
    <>
      {/* for normal services */}
      {rowArray.map((row, i) => {
        return (
          <div
            draggable={false}
            key={i}
            className={`w-full h-[107px] absolute ${
              dragEntered &&
              !service?.service?.is_accessory &&
              !service?.service?.is_accessory_rail
                ? "border-dashed border-y-2 border-slate-600"
                : dragEntered && "z-10"
            }`}
            // style={{ transform: `translate(0, ${row}px)` }}
            style={{ top: `${row}px` }}
            onDragEnter={(e) => {
              e.stopPropagation();
              setDragEntered(true);
            }}
          >
            {rail.services
              .filter((serv) => serv.positions.y === row && !serv.is_slide)
              .map((ser, serviceIndex) => {
                return (
                  <div
                    ref={wrapperRef}
                    id="service-id"
                    draggable
                    key={serviceIndex}
                    className={`absolute flex justify-center
                  ${dragEntered ? "non_dropable " : ""}`}
                    style={generateStyle(ser)}
                    onDragStart={(e) =>
                      handleDragStart(ser, e, railIndex, serviceIndex)
                    }
                    onDragEnter={(e) => e.stopPropagation()}
                    onClick={() => {
                      const rowBlock = document.getElementById("axiom_outer");
                      const fakeEvent = {
                        target: rowBlock,
                        currentTarget: rowBlock,
                        stopPropagation: () => {
                          // No-op or logging for debug
                        },
                      };
                      customEvent(fakeEvent);

                      setService(ser, true);
                      railInd(railIndex);
                      rowInd(serviceIndex);
                    }}
                  >
                    <div
                      draggable={"false"}
                      className=" pointer-events-none"
                      style={{
                        width: serviceWidth(ser),
                        backgroundImage: `url(${alterImageUrl(ser.image_url)})`,
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        pointerEvents: "none",
                        height: ser.height / pixelPerInch,
                      }}
                      onDragEnter={(e) => e.stopPropagation()}
                    />
                  </div>
                );
              })}
          </div>
        );
      })}
      {/* for slides */}
      {rail.services
        .filter((serv) => serv.is_slide)
        .map((ser, serviceIndex) => {
          return (
            <div
              draggable
              key={serviceIndex}
              className={`absolute flex justify-center 
                  ${dragEntered ? "non_dropable " : ""}`}
              style={generateStyle(ser)}
              onDragStart={(e) =>
                handleDragStart(ser, e, railIndex, serviceIndex)
              }
              onDragEnter={(e) => e.stopPropagation()}
              onClick={() => {
                const rowBlock = document.getElementById("axiom_outer");

                const fakeEvent = {
                  target: rowBlock,
                  currentTarget: rowBlock,
                  stopPropagation: () => {
                    // No-op or logging for debug
                  },
                };
                customEvent(fakeEvent);

                setService(ser, true);
                railInd(railIndex);
                rowInd(serviceIndex);
              }}
            >
              <div
                draggable={"false"}
                className=" pointer-events-none"
                style={{
                  width: serviceWidth(ser),
                  backgroundImage: `url(${alterImageUrl(ser.image_url)})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  pointerEvents: "none",
                  height: ser.height / pixelPerInch,
                }}
                onDragEnter={(e) => e.stopPropagation()}
              />
            </div>
          );
        })}
    </>
  );
};
